import { useState } from 'react';
import type { SurveyAudienceMode } from '@engage/types';
import { useCreateSurvey, type Survey } from '../../api/surveys.api';
import { Btn, Icon, ICONS } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

/**
 * Create-a-survey modal. Audience mode is the key choice: anonymous (open
 * public link) vs identified (tokenised links tied to a known contact).
 * Everything else is configured later in the builder.
 */
export function NewSurveyModal(props: { onClose: () => void; onCreated: (survey: Survey) => void }) {
  const [name, setName] = useState('');
  const [audienceMode, setAudienceMode] = useState<SurveyAudienceMode>('ANONYMOUS');
  const [error, setError] = useState<string | null>(null);
  const mutation = useCreateSurvey();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      const survey = await mutation.mutateAsync({ name: name.trim(), audience_mode: audienceMode });
      props.onCreated(survey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create survey');
    }
  };

  return (
    <div
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,24,60,.32)',
        backdropFilter: 'blur(1px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '60px 24px',
        zIndex: 200,
        fontFamily: FORM_UI_FONT,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(15,18,60,.28)', overflow: 'hidden' }}
      >
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: 19, fontWeight: 800, color: C.indigo, margin: 0 }}>Create a survey</h2>
            <div style={{ fontSize: 12.5, color: C.sub, marginTop: 3 }}>
              You can change the audience and all other settings later in the builder.
            </div>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: C.faint }}
            aria-label="Close"
          >
            <Icon d={ICONS.x} size={20} color={C.faint} />
          </button>
        </div>

        <form onSubmit={submit} style={{ padding: '22px 24px 24px' }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: C.indigoSoft, letterSpacing: 0.3 }}>SURVEY NAME</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Candidate experience: Q2"
            autoFocus
            style={{
              width: '100%',
              marginTop: 6,
              padding: '11px 13px',
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              fontFamily: FORM_UI_FONT,
              fontSize: 13.5,
              color: C.ink,
              boxSizing: 'border-box',
            }}
          />

          <label
            style={{ fontSize: 12, fontWeight: 700, color: C.indigoSoft, display: 'block', margin: '20px 0 8px', letterSpacing: 0.3 }}
          >
            AUDIENCE
          </label>
          <div style={{ display: 'flex', gap: 12 }}>
            <AudienceCard
              selected={audienceMode === 'ANONYMOUS'}
              onSelect={() => setAudienceMode('ANONYMOUS')}
              title="Anonymous"
              body="Open public link: anyone with the URL can respond. Great for embeds, NPS and broad feedback. Dedupe by email/phone if you need one response per person."
            />
            <AudienceCard
              selected={audienceMode === 'IDENTIFIED'}
              onSelect={() => setAudienceMode('IDENTIFIED')}
              title="Identified"
              body="Tokenised links sent to known candidates/contacts. Responses are tied to that person and can be reminded automatically until complete."
            />
          </div>

          {error && (
            <div style={{ marginTop: 16, padding: '10px 12px', background: C.dangerBg, color: C.danger, borderRadius: 9, fontSize: 12.5, fontWeight: 600 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
            <Btn kind="ghost" onClick={props.onClose}>
              Cancel
            </Btn>
            <Btn kind="primary" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create & open builder'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

function AudienceCard(props: { selected: boolean; onSelect: () => void; title: string; body: string }) {
  const col = props.selected ? C.indigo : C.line;
  return (
    <button
      type="button"
      onClick={props.onSelect}
      style={{
        flex: 1,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: FORM_UI_FONT,
        background: props.selected ? C.candidateBg : '#fff',
        borderRadius: 12,
        padding: '15px 15px',
        border: `2px solid ${col}`,
        transition: 'all .15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: C.indigo }}>{props.title}</span>
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 99,
            border: `2px solid ${props.selected ? C.indigo : C.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {props.selected && <span style={{ width: 9, height: 9, borderRadius: 99, background: C.indigo }} />}
        </span>
      </div>
      <div style={{ fontSize: 11.8, color: C.sub, lineHeight: 1.5 }}>{props.body}</div>
    </button>
  );
}
